import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { joiResolver } from '@hookform/resolvers/joi';
import ApolloError from '@shared/components/ApolloError';
import ErrorMessage from '@shared/components/ErrorMessage';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import PageInstructions from '@shared/components/PageInstructions';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import ConfirmDialogWithButton from '@shared/components/confirmDialog/ConfirmDialogWithButton';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  CHANGE_DATE_REQUEST_ACTION_TYPE,
  CHANGE_DATE_REQUEST_STATUS
} from '../../calendar/constants/ChangeDateRequestConstants';
import FDA_REVIEW_CHANGE_DATE_REQUEST from '../queries/fdaReviewChangeDateRequestMutation';
import { Page } from '../../layout';
import {
  CHANGE_DATE_REQUEST_STATUS_DISPLAY_NAMES_MAP,
  COMMENT_MAX_LENGTH,
  COMMENT_MIN_LENGTH
} from '../constants/DateChangeReviewConstants';
import GET_EVENT_CHANGE_DATE_REVIEW from '../queries/getEventChangeDateReviewQuery';
import DateChangeReviewValidator from '../validators/DateChangeReviewValidator';
import EventDateChangeReviewMapper from '../mappers/EventDateChangeReviewMapper';

const EventDateChangeReviewPage = () => {
  const { requestId } = useParams();
  const requestIdValue = parseInt(requestId);

  const navigate = useNavigate();

  const form = useForm({
    resolver: joiResolver(DateChangeReviewValidator.schema),
    defaultValues: { DeclineComment: '', Status: '' },
    mode: 'onChange'
  });

  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
    trigger
  } = form;

  const {
    data: dateChangeReviewData,
    loading: dateChangeReviewLoading,
    error: dateChangeReviewError
  } = useQuery(GET_EVENT_CHANGE_DATE_REVIEW, {
    variables: {
      id: requestIdValue
    },
    onCompleted: data => {
      if (data?.eventDateChangeReview?.DeclineComment) {
        reset({
          DeclineComment: data.eventDateChangeReview.DeclineComment
        });
      }
    }
  });

  const [updateDateChangeReview, { error: updateDateChangeReviewError }] = useMutation(
    FDA_REVIEW_CHANGE_DATE_REQUEST
  );

  const review = dateChangeReviewData?.eventDateChangeReview || {};
  const isFDAReviewerStatus = review.Status === CHANGE_DATE_REQUEST_STATUS.PENDING_FDA_APPROVAL;

  const reviewerName =
    review.ActionType === CHANGE_DATE_REQUEST_ACTION_TYPE.SYSTEM_ACTION
      ? autoApprovedReviewer
      : review.FDAReviewerName;

  const breadcrumbPathItems = [{ name: pageTitle, url: '' }];
  const annotationItems = [
    review.MddapOrgUnit?.MddapOrganization?.Name,
    review.MddapOrgUnit?.Name,
    review.AppraisalProgramYear
  ];

  const handleOnConfirm = async formData => {
    await updateDateChangeReview({
      variables: {
        requestData: EventDateChangeReviewMapper.ToApolloInput(formData, review)
      }
    });

    navigate('/data-reports', {
      state: {
        successMessage:
          formData.Status === CHANGE_DATE_REQUEST_STATUS.APPROVED
            ? approvedSuccessMessage
            : declineSuccessMessage
      }
    });
  };

  const updateFormStatus = value => {
    form.setValue('Status', value);
  };

  const openModalPreCondition = async () => {
    return await trigger();
  };

  if (dateChangeReviewData?.eventDateChangeReview === null) {
    return <Navigate to='/permissions' />;
  }

  if (dateChangeReviewError || updateDateChangeReviewError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='fda-review-page'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={dateChangeReviewLoading}>
        <div className='actions'>
          {isFDAReviewerStatus && (
            <ConfirmDialogWithButton
              openModalBtnText='Approve'
              openModalPreCondition={openModalPreCondition}
              openModalBtnHandleOnClick={() =>
                updateFormStatus(CHANGE_DATE_REQUEST_STATUS.APPROVED)
              }
              handleOnConfirm={handleSubmit(handleOnConfirm)}
              message={approveModalTitle}
            />
          )}
        </div>
      </Breadcrumb>

      <section className='page-instructions'>
        <PageInstructions title={pageTitle} description={description} />
      </section>

      <div className='input-area'>
        <div className='label bold'>Status:</div>
        <SkeletonWrapper show={dateChangeReviewLoading}>
          <div className='value'>
            {CHANGE_DATE_REQUEST_STATUS_DISPLAY_NAMES_MAP.get(review.Status)}
          </div>
        </SkeletonWrapper>
      </div>

      <div className='input-area'>
        <div className='label bold'>Reviewer:</div>
        <SkeletonWrapper show={dateChangeReviewLoading}>
          <div className='value'> {reviewerName || defaultReviewer}</div>
        </SkeletonWrapper>
      </div>

      <div className='input-area'>
        <div className='label bold'>Rationale:</div>
        <SkeletonWrapper show={dateChangeReviewLoading}>
          <div className='value'> {review.SubmitComment}</div>
        </SkeletonWrapper>
      </div>

      <div className='input-area'>
        <div className='label bold'>Appraisal Date Change:</div>
        <SkeletonWrapper show={dateChangeReviewLoading}>
          <div className='value'>
            {review.StartDate} - {review.EndDate}
          </div>
        </SkeletonWrapper>
      </div>

      <form>
        <FormProvider {...form}>
          <div className='input-container'>
            <div className='input-area'>
              <div className='label bold'>Comments:</div>
              <Controller
                control={control}
                name='DeclineComment'
                render={({ field }) => (
                  <MuiInputTextWithRemainingChars
                    className='value'
                    error={!!errors.DeclineComment}
                    disableUnderline
                    multiline={true}
                    rows={5}
                    value={field.value}
                    onChange={field.onChange}
                    disabled={dateChangeReviewLoading || !isFDAReviewerStatus}
                    maxLength={COMMENT_MAX_LENGTH}
                    minLength={COMMENT_MIN_LENGTH}
                    hideRemainingChars={false}
                  />
                )}
              />
            </div>

            {isFDAReviewerStatus && (
              <div className='return-action'>
                <ConfirmDialogWithButton
                  openModalBtnText='Decline'
                  openModalBtnType='secondary-button'
                  openModalPreCondition={openModalPreCondition}
                  openModalBtnHandleOnClick={() =>
                    updateFormStatus(CHANGE_DATE_REQUEST_STATUS.DECLINED)
                  }
                  handleOnConfirm={handleSubmit(handleOnConfirm)}
                  message={declineModalTitle}
                />
                {errors.DeclineComment && (
                  <ErrorMessage message={errors.DeclineComment.message} className='error-message' />
                )}
              </div>
            )}
          </div>
        </FormProvider>
      </form>
    </Page>
  );
};

const pageTitle = 'Date Change Review';

const description =
  'After reviewing the date change request, select either the “Approve” button to inform the Lead Appraiser ' +
  'that they may proceed with the new appraisal date, or select the “Decline” button to decline the date change request ' +
  'and inform the Lead Appraiser of the rationale (in the open text box). The date change request will be automatically ' +
  'approved if no action is taken after 3 business days from submission.';

const approveModalTitle = 'Are you sure you want to approve?';
const declineModalTitle =
  'Are you sure you want to Return the Date Change request to the Requestor?';

const approvedSuccessMessage = 'Date Change has been successfully Approved.';
const declineSuccessMessage = 'Date Change has been successfully Declined.';
const defaultReviewer = 'TBD';
const autoApprovedReviewer = 'Auto';

export default EventDateChangeReviewPage;
